import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { Employee, UserRole } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactNode } from 'react';
import { connect } from 'react-redux';
import { hasRole, padZeroLeft } from '@synergy/utils';
import urlJoin from 'url-join';

import { AppState } from '../../../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../../../../../common/components/autocomplete-list/pool-autocomplete-list';
import { PoolAutocompleteListFooter } from '../../../../../../common/components/autocomplete-list/pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../../../../../common/components/abstract/form-autocomplete-list';
import { ROUTES } from '../../../../../sidebar-menu/navigation-drawer/routes';
import { SupplierEmployeeApi } from '../api/supplier-employees-api';
import { User } from '../../../../../../common/models/user';

export interface PartialEmployee extends BaseAutocompleteSuggestionType, Pick<Employee, 'fullName' | 'email'> {
    spacer: ReactNode;
}

interface SupplierEmployeeAutocompleteProps {
    accountId?: number;
}

interface SupplierEmployeeAutocompleteReduxProps {
    user: User;
}

class BaseSupplierEmployeeAutocomplete extends PoolFormAutocompleteList<
    Employee,
    PartialEmployee,
    SupplierEmployeeAutocompleteProps & SupplierEmployeeAutocompleteReduxProps
> {
    private api = new SupplierEmployeeApi(API_BASE_URL);

    public label = (<FormattedMessage id="app.contactsAndOrganizations.detail.name" />);
    public getSuggestionValue = (suggestion: PartialEmployee) => suggestion?.fullName;
    public hideId = true;

    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.navigation.contacts"
            url={urlJoin(BASE_URL, ROUTES.contactsAndOrganizations.contacts.new)}
        />
    );

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public componentDidUpdate(prevProps: SupplierEmployeeAutocompleteProps) {
        if (this.props.accountId !== prevProps.accountId) {
            this.setState({ items: [] });
        }
    }

    public async handleValueChanged(search: string) {
        if (!this.props.accountId) {
            this.setState({ items: [] });
            return;
        }
        const response = await this.api.searchEmployees(this.props.accountId, search);
        const items = response.success === true ? response.result : [];
        this.setState({ items });
    }

    public getPartial = (items: Employee[]): PartialEmployee[] =>
        items.map(contact => ({
            id: padZeroLeft(contact.id, 5),
            fullName: contact.fullName,
            spacer: <span />,
            email: contact.email,
        }));
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export const SupplierEmployeeAutocomplete = connect(mapStateToProps)(BaseSupplierEmployeeAutocomplete);
